import * as yup from 'yup';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SvgMoney from '@icons/money-inverted.svg?react';
import SvgMessage from '@icons/message.svg?react';
import SvgClose from '@icons/close.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgPlusCircle from '@icons/plus-circle.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgMapPin from '@icons/map-pin.svg?react';
import SvgInfoCircle from '@icons/info.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import DistributionValidTillTag from '@components/framework/tags/DistributionValidTillTag.tsx';
import LadenFactorTag from '@components/framework/tags/LadenFactorTag.tsx';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge.tsx';
import COBanner from '@components/FigmaComponents/COBanner.tsx';
import { COBannerVariant } from '@components/FigmaComponents/COBannerVariant.ts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  dayjsFromApiPrague,
  dayjsFromApiUtc,
  dayjsFromApiUtcInTz,
  formatDateFromDayjsInTzWithLocale,
  formatDateTimeFromDayjsInTzWithLocale,
  formatTimeFromDayjsInTz,
} from '@utils/dateWithTz.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useForm from '@hooks/useForm/useForm.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import InputDateAndTime from '@components/InputDateAndTime/InputDateAndTime.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { CreateSpotState } from '@app/pages/carrier/demand-detail/DemandDetailComponent.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import RejectDialogComponent from '@app/pages/carrier/demand-detail/RejectDialog/RejectDialogComponent.tsx';
import { InputTextarea } from 'primereact/inputtextarea';
import InputPhoneNumber from '@components/RingilComponents/InputPhoneNumber/InputPhoneNumber.tsx';
import {
  CarrierQuoteStateEnum,
  CurrencyLockTypeEnum,
  CustomerQuoteStateEnum,
  DistributionTypeEnum,
  LastExpeditionStatusEnum,
  LovDTO,
  LovTransportationTypeOutDTO,
  TimeSlotQuoteDemandOutDTO,
  TransportationTypeEnum,
} from '@api/ringil-api.v2.tsx';
import { ApiContext } from '@api/api.tsx';
import TransportLegItem from '@app/pages/carrier/demand-detail/TransportLeg.tsx';
import TimeslotItem from '@app/pages/carrier/demand-detail/TimeslotItem.tsx';
import { DemandDetailView } from '@app/pages/carrier/demand-detail/types.ts';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { getTransportIcon, joinLegInfo, OfferTypeEnum } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';

import 'dayjs/locale/en';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/hu';
import 'dayjs/locale/pl';
import { formatPrice } from '@utils/priceUtils.ts';
import { findErrorMessage } from '@utils/validationUtils.ts';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import {
  buildCarrierQuoteAcceptData,
  carrierQuoteInitialForm,
  CarrierQuoteFormData,
} from '@app/pages/carrier/demand-detail/carrierQuoteMapping.ts';
import ContractedPriceRows from '@app/pages/carrier/demand-detail/ContractedPriceRows.tsx';
import {
  contractedCurrency,
  contractedSingleUnitPrice,
  contractedTotal,
  contractedUnitOptions,
  contractedValidTill,
  hasMissingContractedPrice,
  isValidContractedUnits,
} from '@app/pages/carrier/demand-detail/contractedQuote.ts';

dayjs.extend(relativeTime);


interface ComponentProps {
  quote: DemandDetailView;
  messageOnClick?: () => void;
  offerType: OfferTypeEnum;
  onAccept: (data: CreateSpotState, onFinish: () => void) => void;
  onReject: () => void;
  code?: string;
  /** RD-2978: the e-mail button the carrier came by (quick link `action`) - acted on once for a new contracted demand. */
  initialAction?: string;
}

export interface TimeslotData {
  day: string;
  timeFrom?: string;
  timeTo?: string;
  isWholeDay: boolean;
}

export interface Timeslot {
  locationId: string;
  locationName: string;
  streetNr: string;
  zipCode: string;
  city: string;
  country: string;
  useCustomerSlot: boolean;
  customerTimeslots: TimeSlotQuoteDemandOutDTO[];
  carrierTimeslot?: TimeslotData;
  timezoneId: string;
}

type FormData = CarrierQuoteFormData;

const CarrierQuote: React.FC<ComponentProps> = ({
                                                  quote,
                                                  messageOnClick,
                                                  offerType,
                                                  onAccept,
                                                  onReject,
                                                  code,
                                                  initialAction,
                                                }) => {

  const isFixedCurrency = quote.currencyLockType === CurrencyLockTypeEnum.ONLY_SELECTED;
  // CONTRACTED ("Smluvní dopravce"): a carrier quote like SPOT, but the price is given by the contract, the carrier
  // only accepts the whole shipment (no laden factor) or picks the units (laden factor). No price, currency or validTill input.
  const isContractedQuote = quote.distributionType === DistributionTypeEnum.CONTRACTED;
  const contracted = quote.contracted;
  const contractedMissingPrice = isContractedQuote && hasMissingContractedPrice(contracted);
  const contractedCur = isContractedQuote ? contractedCurrency(contracted) : undefined;
  const contractedUnitPrice = isContractedQuote ? contractedSingleUnitPrice(contracted) : null;

  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { lovTransportationTypes, lovNavyShippingCompanies } = useContext(ApiContext);
  const phoneEnabled = quote.carrierQuoteSettings.enforcePhoneNumber;
  const quoteValidTillReadOnly = quote.carrierQuoteSettings.quoteValidTillReadOnly;
  const quoteValidTillVisible = quote.carrierQuoteSettings.quoteValidTillVisible;
  const disableCarrierQuoteTimeslots = quote.carrierQuoteSettings.disableCarrierQuoteTimeslots === true;

  const tiebreaker = quote.tiebreaker;
  const tiebreakerTimezoneId = tiebreaker?.timezoneId
    || loggedUser?.defaultTimezoneId
    || quote.userTimezoneId
    || quote.timezoneId
    || 'Europe/Prague';
  const tiebreakerUtcEndDayjs = tiebreaker?.utcEnd
    ? dayjsFromApiUtcInTz(tiebreaker.utcEnd, tiebreakerTimezoneId)
    : null;
  const isCarrierInTiebreaker = !!tiebreaker
  const isTiebreakerActive = !!tiebreakerUtcEndDayjs
    && tiebreakerUtcEndDayjs.isAfter(dayjs())
    && isCarrierInTiebreaker;
  const effectiveDistributionExpired = isTiebreakerActive ? false : quote.isDistributionExpired;

  const [priceEditMode, setPriceEditMode] = useState<boolean>(
    (
      !effectiveDistributionExpired
      && (quote.carrierQuoteState === CarrierQuoteStateEnum.NEW
        || quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER
      )
    ));
  const [infoEditMode, setInfoEditMode] = useState<boolean>(
    (
      !effectiveDistributionExpired
      && (
        quote.carrierQuoteState === CarrierQuoteStateEnum.NEW
      || quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER
      || (isNullOrUndefined(quote.carrierQuoteState) && offerType === OfferTypeEnum.FIXED)
      )
    )
    && quote.lastExpeditionStatus !== LastExpeditionStatusEnum.ASSIGNED,
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [rejectDialogVisible, setRejectDialogVisible] = useState<boolean>(false);
  const acceptButtonsRef = useRef<HTMLDivElement>(null);

  // "Odmítnout přepravu" from the e-mail opens the reject dialog, "Přijmout smluvní cenu" brings the accept button into
  // view - not sent by itself: the carrier still confirms the time slots (and picks the units with a laden factor)
  useEffect(() => {
    const openContracted = isContractedQuote && !effectiveDistributionExpired && quote.carrierQuoteState === CarrierQuoteStateEnum.NEW;
    if (!openContracted) return;
    if (initialAction === 'reject') setRejectDialogVisible(true);
    if (initialAction === 'confirm') acceptButtonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  const [offerNoteEnabled, setOfferNoteEnabled] = useState<boolean>(!!quote.note);
  const [transportLegsEnabled, setTransportLegsEnabled] = useState<boolean>(quote.transportLegs && quote.transportLegs.length > 0);
  const [timeslotsEnabled, setTimeslotsEnabled] = useState<boolean>(!disableCarrierQuoteTimeslots && offerType === OfferTypeEnum.SPOT && quote.timeslots?.flatMap(timeslot => timeslot.timeslots).some(timeslot => timeslot.isCarrier));

  const timezoneToUse = loggedUser?.defaultTimezoneId || quote.userTimezoneId || quote.timezoneId || 'Europe/Prague'
  const quoteUtcEndDayjs = quote.utcEnd ? dayjsFromApiUtcInTz(quote.utcEnd, quote.timezoneId || timezoneToUse) : null;
  const distributionDayjs = quote.utcEnd ? dayjsFromApiUtcInTz(quote.utcEnd, quote.timezoneId || timezoneToUse) : null;
  const distributionResponse = distributionDayjs
    ? { localDate: distributionDayjs.format('L'), hourString: distributionDayjs.format('HH:mm:ss'), timeZone: quote.timezoneId || timezoneToUse }
    : null;

  const validTillDate = isTiebreakerActive
    ? tiebreakerUtcEndDayjs
    : ((quote.utcEnd && quote.timezoneId) ? quoteUtcEndDayjs : distributionDayjs);

  const localeShort = i18n.language?.split('-')[0]?.toLowerCase() || 'en';
  const supportedLocales = ['en', 'cs', 'de', 'fr', 'hu', 'pl'];
  const dayjsLocale = supportedLocales.includes(localeShort) ? localeShort : 'en';
  const effectiveTimezone = isTiebreakerActive ? tiebreakerTimezoneId : timezoneToUse;
  const timeLeft = validTillDate ? dayjs().tz(effectiveTimezone).locale(dayjsLocale).to(validTillDate) : '';

  const ladenFactorOptions = useMemo(() => {
    const remainingUnits = quote.remainingManipulationUnits || quote.handlingUnitCount;
    if (isContractedQuote) {
      return contractedUnitOptions(remainingUnits, quote.ladenFactor, quote.allowPartialDelivery).map(v => ({ value: v, label: v.toString() }));
    }
    if (!remainingUnits || !quote.ladenFactor) return [];
    const options = [];
    for (let i = quote.ladenFactor; i <= remainingUnits; i += quote.ladenFactor) {
      options.push({ value: i, label: i.toString() });
    }
    return options;
  }, [quote.handlingUnitCount, quote.remainingManipulationUnits, quote.ladenFactor, quote.allowPartialDelivery, isContractedQuote]);

  const [transportationTypes, setTransportationTypes] = useState<LovTransportationTypeOutDTO[]>([]);
  const [navyShippingCompanies, setNavyShippingCompanies] = useState<LovDTO[]>([]);

  useEffect(() => {
    lovTransportationTypes(d => {
      setTransportationTypes(d);
      },
      code,
    );
    lovNavyShippingCompanies(d => {
      setNavyShippingCompanies(d);
      },
      code,
    );
  }, [lovNavyShippingCompanies, lovTransportationTypes]);

  const schema = yup.object().shape({
    // CONTRACTED: price, currency and validTill are not entered by the carrier (backend computes them)
    ...(!isContractedQuote && {
      price: yup.number().defined().required(),
      currency: yup.string().defined().required(),
      validTillLocalTime: yup.string().defined().required(),
    }),
    ...(quote.handlingUnitCount && quote.ladenFactor && !isContractedQuote && {
      partialDeliveryUnits: yup.number().defined().required(),
      unitPrice: yup.number().defined().required().positive(),
    }),
    ...(isContractedQuote && quote.ladenFactor && {
      partialDeliveryUnits: yup
        .number()
        .defined()
        .required()
        .test('contractedUnits', tr('carrier.demand.quote.contracted.error.invalidUnits', 'Neplatný počet jednotek'), v =>
          isValidContractedUnits(v, quote.remainingManipulationUnits || quote.handlingUnitCount, quote.ladenFactor, quote.allowPartialDelivery),
        ),
    }),
    ...(phoneEnabled && {
      phonePrefix: yup.string().defined().required(tr('validation.phoneNumberRequired', 'Phone number is required')),
      phoneNumber: yup.string().defined().required().min(5, tr('validation.phoneNumberMinLength', 'Phone number minimum length is 5')),
    }),
    timeslots: yup.array().of(
      yup.object().shape({
        locationId: yup.string().required(),
        useCustomerSlot: yup.boolean(),
        carrierTimeslot: yup
          .object()
          .nullable()
          .shape({
            day: yup.string().required(),
            timeFrom: yup.string().nullable(),
            timeTo: yup.string().nullable(),
          })
          .test('typeError', function(value) {
            if (value == null || value.isWholeDay) return true;
            if (!value.timeFrom) {
              return this.createError({
                path: `${this.path}.timeFrom`,
                message: 'Time from is required',
              });
            }
            return true;
          })
          .test('typeError', function(value) {
            if (value == null || value.isWholeDay) return true;
            if (!value || !value.timeTo) {
              return this.createError({
                path: `${this.path}.timeTo`,
                message: 'Time to is required',
              });
            }
            return true;
          }),
      }),
    ),
  });

  const form = useForm<FormData>(
      schema,
      carrierQuoteInitialForm({
        quote,
        quoteValidTillReadOnly,
        phoneEnabled,
        isSpotOffer: offerType === OfferTypeEnum.SPOT,
        timezoneToUse,
        contracted: isContractedQuote ? { currency: contractedCur } : undefined,
      }),
      (d) => save(d),
      false,
      false,
      () => {
      },
      'none',
    )
  ;

  // Sync unitPrice and partialDeliveryUnits to calculate total price for laden factor
  useEffect(() => {
    if (quote.ladenFactor && form.values.unitPrice !== undefined && form.values.partialDeliveryUnits) {
      form.setFieldValue(form.names.price, form.values.unitPrice * form.values.partialDeliveryUnits);
    }
  }, [form.values.unitPrice, form.values.partialDeliveryUnits, quote.ladenFactor, form]);

  // CONTRACTED preview total: units × unit price with laden factor, the whole-shipment total otherwise
  const contractedOfferTotal = isContractedQuote
    ? contractedTotal(contracted, quote.ladenFactor, quote.ladenFactor ? form.values.partialDeliveryUnits : null)
    : null;

  const save = (data: FormData) => {
    setLoading(true);
    const contractedOffer = isContractedQuote
      ? {
          total: contractedOfferTotal,
          currency: contractedCur,
          validTill: contractedValidTill(quoteUtcEndDayjs, dayjs()),
          timezoneId: quote.timezoneId || timezoneToUse,
          wholeShipment: !quote.ladenFactor,
        }
      : undefined;
    onAccept(
      buildCarrierQuoteAcceptData(data, phoneEnabled, timezoneToUse, contractedOffer),
      () => {
        setLoading(false);
        setPriceEditMode(false);
        setInfoEditMode(false);
      },
    );
  };

  const isMyQuoteBeforeDistribution = useMemo(() => {
    if (isContractedQuote) return false;
    const myValidTill = (priceEditMode || infoEditMode)
      ? form.values.validTillLocalTime
      : quote.myQuoteInfo?.utcEnd;

    return !!myValidTill &&
      !!quote.utcEnd &&
      dayjs(myValidTill).isBefore(dayjs(quote.utcEnd));
  }, [
    form.values.validTillLocalTime,
    priceEditMode,
    infoEditMode,
    quote.myQuoteInfo?.myQuoteValidTill,
    quote.myQuoteInfo?.utcEnd,
    quote.distributionValidTill,
    quote.utcEnd,
    quote.timezoneId,
    isContractedQuote,
  ]);


  return (
    <div
      className="flex flex-col p-6 rounded-[8px] max-w-3xl bg-base-tertiary shadow-[0_0_8px_0_rgba(0,0,0,0.25)]"
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-2 items-center">
          <SvgMoney className="w-8 h-8" />
          <COHeadline
            variant="h2"
            title={tr('carrier.demand.quote.title', 'Your quote')}
          />
        </div>
        {messageOnClick && (
          <RingilButton
            mode={'outlined-rectangle'}
            icon={<SvgMessage className="text-content-brand" />}
            label={tr('carrier.demand.quote.button.writeToCustomer.label', 'Write to customer')}
            onClick={messageOnClick}
          />
        )}
      </div>
      <div className="flex flex-row mt-2 gap-2 justify-start flex-wrap">
        {(quote.utcEnd || quote.distributionValidTill) && (
          <span>
          <DistributionValidTillTag
            dateTimeResponse={distributionResponse}
            utcDateTime={quoteUtcEndDayjs}
            userTimezoneId={timezoneToUse}
            onChanged={() => {
            }}
            readonly
            dataTestId="carrier-demand-validTill"
          />
        </span>
        )}
        {quote.ladenFactor &&
          <LadenFactorTag ladenFactor={quote.ladenFactor} />
        }
      </div>
      <div className="flex flex-row mt-2 gap-2 justify-start flex-wrap">
        <CreatedBadge createdAt={quote.distributionCreatedAt} createdBy={quote.responsiblePerson.fullName} timezoneId={timezoneToUse} dataTestId="carrier-demand-createdAt"/>
      </div>
      <div
        className="flex flex-col mt-2 bg-base-secondary border border-solid border-content-quaternary rounded-[8px] shadow-[0_0_4px_0_rgba(0,0,0,0.1)]">
        {(quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER || quote.customerQuoteState === CustomerQuoteStateEnum.REJECTED_BY_CARRIER) && (
          <COBanner
            variant={COBannerVariant.Error}
            label={tr('carrier.demand.quote.banner.rejectedByCarrier.label', 'Demand was rejected by your company.')}
          />
        )}
        {quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER && !quote.isAssignedToAnotherCarrier && (
          <COBanner
            variant={COBannerVariant.Error}
            label={tr('carrier.demand.quote.banner.rejectedByCustomer.label', 'Customer rejected Your offer.')}
          />
        )}
        {quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER && quote.isAssignedToAnotherCarrier && (
          <COBanner
            variant={COBannerVariant.Error}
            label={tr('carrier.demand.quote.banner.rejectedByCustomer.otherCarrierWin.label', 'Shipment was assigned to other carrier')}
          />
        )}
        {(quote.carrierQuoteState === CarrierQuoteStateEnum.NEW || quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CARRIER || quote.carrierQuoteState === CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER)
          && !quote.isAssignedToAnotherCarrier
          && (isNullOrUndefined(quote.ladenFactor) || quote.remainingManipulationUnits > 0)
          && timeLeft && (
          <COBanner
            variant={COBannerVariant.Warning}
            label={tr('carrier.demand.quote.banner.timeLeft.label', 'Demand is waiting for a quote. Deadline {{timeLeft}} ', { timeLeft })}
          />
        )}
        {isTiebreakerActive && (
          <COBanner
            variant={COBannerVariant.Warning}
            label={tr(
              'carrier.demand.quote.banner.tiebreakActive.label',
              'Carrier tiebreak in progress. Submit your final offer before {{-deadline}}.',
              { deadline: formatDateTimeFromDayjsInTzWithLocale(tiebreakerUtcEndDayjs, tiebreakerTimezoneId) },
            )}
          />
        )}
        {quote.carrierQuoteState === CarrierQuoteStateEnum.OFFERED_PRICE && !isContractedQuote && (
          <COBanner
            variant={COBannerVariant.Success}
            label={tr('carrier.demand.quote.banner.offeredPrice.label', 'Quote offered. Waiting for customer')}
          />
        )}
        {quote.carrierQuoteState === CarrierQuoteStateEnum.OFFERED_PRICE && isContractedQuote && (
          <COBanner
            variant={COBannerVariant.Success}
            label={
              quote.ladenFactor && !isNullOrUndefined(quote.myQuoteInfo.myQuoteManipulationUnits)
                ? tr('carrier.demand.quote.contracted.banner.offeredUnits', 'Přijato: {{units}} jednotek za {{price}} – čeká na potvrzení zákazníkem', {
                    units: quote.myQuoteInfo.myQuoteManipulationUnits,
                    price: formatPrice(quote.myQuoteInfo.myQuotePrice, quote.myQuoteInfo.myQuoteCurrency),
                  })
                : tr('carrier.demand.quote.contracted.banner.offeredWhole', 'Přijato: celá zásilka za {{price}} – čeká na potvrzení zákazníkem', {
                    price: formatPrice(quote.myQuoteInfo.myQuotePrice, quote.myQuoteInfo.myQuoteCurrency),
                  })
            }
          />
        )}
        {
          (
            quote.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER
            || quote.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM
            || (quote.customerQuoteState === CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER && offerType === OfferTypeEnum.FIXED)
          )
          && (
            <COBanner
              variant={COBannerVariant.Success}
              label={tr('carrier.demand.quote.banner.acceptedByCustomer.label', 'Offer accepted by customer')}
            />
          )}

        <div className="flex flex-col p-8 gap-4">
          {priceEditMode && (
            <>
              <COHeadline
                variant="h2"
                title={isContractedQuote
                  ? tr('carrier.demand.quote.contracted.title', 'Smluvní cena')
                  : tr('carrier.demand.quote.offer.title', 'Offered price')}
                className="justify-center mt-4"
              />
              {isContractedQuote && (
                <>
                  <COInlineMessage
                    variant={'alert'}
                    icon={<SvgInfoCircle className="text-content-tertiary" />}
                  >
                    <span data-testid="carrier-demand-quote-contracted-locked">
                      {tr('carrier.demand.quote.contracted.info.locked', 'Cena je smluvně daná, nelze ji měnit.')}
                    </span>
                  </COInlineMessage>
                  <div className="text-content-primary font-dm-sans text-[14px] font-normal">
                    <ContractedPriceRows contracted={contracted} dataTestId="carrier-demand-quote-contracted-prices" />
                  </div>
                  {contractedMissingPrice && (
                    <COInlineMessage variant="warning">
                      {tr(
                        'carrier.demand.quote.contracted.error.missingPrice',
                        'Pro některou manipulační jednotku chybí smluvní cena, nabídku nelze přijmout. Kontaktujte zákazníka.',
                      )}
                    </COInlineMessage>
                  )}
                </>
              )}
              {quote.ladenFactor && (
                <div>
                  <COInlineMessage
                    variant={'alert'}
                    icon={<SvgInfoCircle className="text-content-tertiary" />}
                  >
                    <div className="flex flex-col gap-1">
                      <span>
                        {tr('carrier.demand.quote.offer.partialDelivery.info.requested.prefix', 'Customer requests')}{' '}
                        <strong>
                          {tr('carrier.demand.quote.offer.partialDelivery.info.requested.value', 'to transfer {{manipulationUnits}} MU', {
                            manipulationUnits: quote.remainingManipulationUnits,
                          })}
                        </strong>
                        .
                      </span>
                      <span>
                        {tr('carrier.demand.quote.offer.partialDelivery.info.capacity.prefix', 'Capacity of one vehicle is')}{' '}
                        <strong>
                          {tr('carrier.demand.quote.offer.partialDelivery.info.capacity.value', '{{ladenFactor}} MU', {
                            ladenFactor: quote.ladenFactor,
                          })}
                        </strong>
                        .
                      </span>
                    </div>
                  </COInlineMessage>
                  <Field
                    label={tr('carrier.demand.quote.offer.partialDelivery.label', 'How many units will be delivered')}
                    required={quote.ladenFactor && !form.values.partialDeliveryUnits}
                    disabled={!quote.allowPartialDelivery}
                    helpText={tr('carrier.demand.quote.offer.partialDelivery.help', 'Partial delivery help')}
                    errorMessage={findErrorMessage(form.validationErrors, form.names.partialDeliveryUnits, tr)}
                  >
                    <Dropdown
                      data-testid="carrier-demand-quote-partialDeliveryUnits"
                      options={ladenFactorOptions}
                      optionValue="value"
                      optionLabel="label"
                      value={form.values.partialDeliveryUnits}
                      onChange={(e) => form.setFieldValue(form.names.partialDeliveryUnits, e.value)}
                      style={{ width: '100%', padding: '0.5rem', color: 'var(--ringil3-gr4)' }}
                    />
                  </Field>
                </div>
              )}
              {isContractedQuote ? (
                <div data-testid="carrier-demand-quote-contracted-total" data-total-price={contractedOfferTotal ?? ''}>
                  <COHeadline
                    variant="h4"
                    title={
                      contractedOfferTotal === null
                        ? tr('carrier.demand.quote.contracted.total.unknown', 'Celkem: -')
                        : quote.ladenFactor && contractedUnitPrice !== null
                          ? tr('carrier.demand.quote.contracted.total.units', 'Celkem: {{units}} × {{unitPrice}} = {{total}}', {
                              units: form.values.partialDeliveryUnits,
                              unitPrice: formatPrice(contractedUnitPrice, contractedCur),
                              total: formatPrice(contractedOfferTotal, contractedCur),
                            })
                          : tr('carrier.demand.quote.contracted.total.wholeShipment', 'Celkem za celou zásilku: {{total}}', {
                              total: formatPrice(contractedOfferTotal, contractedCur),
                            })
                    }
                    icon={<SvgInfoCircle className="text-content-tertiary" />}
                    className="justify-start mb-4"
                  />
                </div>
              ) : quote.ladenFactor ? (
                <>
                  <Field
                    label={tr('carrier.demand.quote.offer.unitPrice.label', 'Price per unit')}
                    required={!form.values.unitPrice}
                    skipCurrencyCssMode={true}
                    errorMessage={findErrorMessage(form.validationErrors, form.names.unitPrice, tr)}
                  >
                    <InputPriceWithCurrency
                      value={form.values.unitPrice || 0}
                      currency={form.values.currency}
                      changePrice={v => form.setFieldValue(form.names.unitPrice, v)}
                      changeCurrency={v => form.setFieldValue(form.names.currency, v)}
                      disabledCurrency={isFixedCurrency}
                      code={code}
                      companyId={quote.companyId}
                      dataTestId="carrier-demand-quote-price"
                      dataTestIdCurrency="carrier-demand-quote-currency"
                    />
                  </Field>
                  <COHeadline
                    variant="h4"
                    title={tr(
                      'carrier.demand.quote.offer.totalPrice.info',
                      'Total price: {{price}} {{currency}} (for {{units}} HU)',
                      {
                        price: form.values.price.toFixed(2),
                        currency: form.values.currency,
                        units: form.values.partialDeliveryUnits,
                      },
                    )}
                    icon={<SvgInfoCircle className="text-content-tertiary"/>}
                    className="justify-start mb-4"
                  />
                </>
              ) : (
                <Field
                  label={tr('carrier.demand.quote.offer.price.label', 'Price')}
                  required={!form.values.price}
                  skipCurrencyCssMode={true}
                  errorMessage={findErrorMessage(form.validationErrors, form.names.price, tr)}
                >
                  <InputPriceWithCurrency
                    value={form.values.price}
                    currency={form.values.currency}
                    changePrice={v => form.setFieldValue(form.names.price, v)}
                    changeCurrency={v => form.setFieldValue(form.names.currency, v)}
                    disabledCurrency={isFixedCurrency}
                    code={code}
                    companyId={quote.companyId}
                    dataTestId="carrier-demand-quote-price"
                    dataTestIdCurrency="carrier-demand-quote-currency"
                  />
                </Field>
              )}
              {quoteValidTillVisible && !isContractedQuote && (
                <Field
                  label={tr('carrier.demand.quote.offer.offerValidTill.label', 'Offer valid till')}
                  required={!quoteValidTillReadOnly && !form.values.validTillLocalTime}
                  readOnly={quoteValidTillReadOnly}
                >
                  <InputDateAndTime
                    value={form.values.validTillLocalTime}
                    onChange={(localDate, timezoneId) => {
                      form.setFieldValue(form.names.validTillLocalTime, localDate as dayjs.Dayjs);
                      form.setFieldValue(form.names.validTillTimezone, timezoneId);
                    }}
                    timeZone={form.values.validTillTimezone}
                    quickPrefills={[
                      {
                        value: 1,
                        unit: 'day',
                        label: tr('carrier.demand.quote.offer.offerValidTill.prefill.oneDay', '1 day'),
                      },
                      {
                        value: 2,
                        unit: 'day',
                        label: tr('carrier.demand.quote.offer.offerValidTill.prefill.twoDays', '2 days'),
                      },
                      quoteUtcEndDayjs ? {
                        staticValue: quoteUtcEndDayjs.add(8, 'hour'),
                        label: tr('carrier.demand.quote.offer.offerValidTill.prefill.eightHoursAfter', '8 hours after valid till'),
                      } : distributionDayjs ? {
                        staticValue: distributionDayjs.add(8, 'hour'),
                        label: tr('carrier.demand.quote.offer.offerValidTill.prefill.eightHoursAfter', '8 hours after valid till'),
                      } : null,
                    ]}
                    fullWidth
                    code={code}
                    dataTestIdCalendar="carrier-demand-quote-validTill-calendar"
                    dataTestIdTimezone="carrier-demand-quote-validTill-calendar-timezone"
                  />
                </Field>
              )}
            </>
          )}
          {!priceEditMode && (
            <>
              <COHeadline
                variant="h4"
                title={isContractedQuote
                  ? tr('carrier.demand.quote.contracted.acceptedPrice.title', 'Přijatá smluvní cena')
                  : tr('carrier.demand.quote.offeredPrice.title', 'Offered price')}
                className="justify-center mt-4"
              />
              <div>
                <COHeadline
                  variant="h1"
                  title={formatPrice(quote.myQuoteInfo.myQuotePrice, quote.myQuoteInfo.myQuoteCurrency)}
                  className="justify-center mt-4"
                />
                {offerType === OfferTypeEnum.SPOT && !isNullOrUndefined(quote.myQuoteInfo.myQuotePrice) && !isNullOrUndefined(quote.myQuoteInfo.myQuoteManipulationUnits) && (
                  <div
                    className="w-full text-content-secondary font-dm-sans text-[13px] font-normal items-center justify-center text-center">
                    {tr(
                      'carrier.demand.quote.offer.pricePerUnit',
                      '{{pricePerUnit}} {{currency}} / Handling unit',
                      {
                        pricePerUnit: (quote.myQuoteInfo.myQuotePrice / quote.myQuoteInfo.myQuoteManipulationUnits).toFixed(2),
                        currency: quote.myQuoteInfo.myQuoteCurrency,
                      },
                    )}
                  </div>
                )}
                {offerType === OfferTypeEnum.FIXED && !isNullOrUndefined(quote.price) && !isNullOrUndefined(quote.handlingUnitCount) && quote.handlingUnitCount !== 0 && (
                  <div
                    className="w-full text-content-secondary font-dm-sans text-[13px] font-normal items-center justify-center text-center">
                    {tr(
                      'carrier.demand.quote.offer.pricePerUnit',
                      '{{pricePerUnit}} {{currency}} / Handling unit',
                      {
                        pricePerUnit: (quote.price / quote.handlingUnitCount).toFixed(2),
                        currency: quote.currency,
                      },
                    )}
                  </div>
                )}

              </div>
              {quote.ladenFactor && (
                <>
                  <COHeadline
                    variant="h4"
                    title={tr('carrier.demand.quote.offer.offeredManipulationUnits.title', 'Offered manipulation units')}
                    className="justify-center mt-4"
                  />
                  <div
                    className="w-full text-content-secondary font-dm-sans text-[13px] font-normal items-center justify-center text-center break-words whitespace-normal">
                    {tr(
                      'carrier.demand.quote.offer.offeredManipulationUnits.label',
                      '{{offeredManipulationUnits}} HU - {{numberOfTransports}} transport units',
                      {
                        offeredManipulationUnits: quote.myQuoteInfo.myQuoteManipulationUnits,
                        numberOfTransports: quote.myQuoteInfo.myQuoteManipulationUnits / quote.ladenFactor,
                      },
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <div className="h-px w-full bg-content-tertiary" />

          {infoEditMode && (
            <div className="flex flex-col gap-4">
              <COHeadline
                variant="h2"
                title={tr('carrier.demand.quote.additionalIndo.title', 'Additional information')}
                className="justify-center mt-4"
              />

              {phoneEnabled && (
                <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                  <Field
                    label={tr('carrier.demand.quote.phone.field.label', 'Phone number')}
                    required={!form.values.phoneNumber}
                    errorMessage={findErrorMessage(form.validationErrors, form.names.phoneNumber, tr)}
                  >
                    <InputPhoneNumber
                      prefix={form.values.phonePrefix}
                      number={form.values.phoneNumber}
                      onPrefixChange={v => form.setFieldValue(form.names.phonePrefix, v)}
                      onNumberChange={v => form.setFieldValue(form.names.phoneNumber, v)}
                    />
                  </Field>
                </div>
              )}

              {transportLegsEnabled && (
                <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                  <div className="flex flex-row justify-between pb-6">
                    <COHeadline
                      variant="h3"
                      title={tr('carrier.demand.quote.transportLegs.title', 'Transport legs')}
                    />
                    <RingilButton
                      mode={'table'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        setTransportLegsEnabled(false);
                        form.setFieldValue(form.names.transportLegs, null);
                      }}
                    />
                  </div>
                  {form.values.transportLegs?.map((leg, idx) => (
                    <div key={idx}>
                      <TransportLegItem
                        idx={idx}
                        leg={leg}
                        transportationTypes={transportationTypes.map(item => ({
                          code: item.transportationType,
                          label: item.name,
                        }))}
                        navyShippingCompanies={navyShippingCompanies}
                        legsLength={form.values.transportLegs.length}
                        onChange={(i, updatedLeg, action) => {
                          const newLegs = [...form.values.transportLegs];
                          if (action === 'moveUp' && i > 0) {
                            [newLegs[i - 1], newLegs[i]] = [newLegs[i], newLegs[i - 1]];
                          } else if (action === 'moveDown' && i < newLegs.length - 1) {
                            [newLegs[i], newLegs[i + 1]] = [newLegs[i + 1], newLegs[i]];
                          } else if (updatedLeg === null) {
                            newLegs.splice(i, 1);
                            if (newLegs.length === 0) {
                              setTransportLegsEnabled(false);
                              form.setFieldValue(form.names.transportLegs, null);
                              return;
                            }
                          } else {
                            newLegs[i] = updatedLeg;
                          }
                          form.setFieldValue(form.names.transportLegs, newLegs);
                        }}
                      />
                      {idx < form.values.transportLegs.length - 1 && (
                        <div className="h-px w-full bg-content-tertiary my-6" />
                      )}
                    </div>
                  ))}
                  <div className="mt-10">
                    <RingilButton
                      mode={'outlined-rectangle'}
                      icon={<SvgPlusCircle className="text-content-brand" />}
                      label={tr('carrier.demand.quote.button.addTransportationLeg.label', 'Add new leg')}
                      onClick={() => {
                        const newLegs = [...(form.values.transportLegs || [])];
                        newLegs.push({
                          transportationType: TransportationTypeEnum.GROUND,
                          position: newLegs.length,
                        });
                        form.setFieldValue(form.names.transportLegs, newLegs);
                      }}
                    />
                  </div>
                </div>
              )}

              {timeslotsEnabled && !disableCarrierQuoteTimeslots && (
                <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                  <div className="flex flex-row justify-between pb-6">
                    <COHeadline
                      variant="h3"
                      title={tr('carrier.demand.quote.timeslots.title', 'Timeslots')}
                    />
                    <RingilButton
                      mode={'table'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        setTimeslotsEnabled(false);
                        form.setFieldValue(
                          form.names.timeslots,
                          form.values.timeslots.map(ts => ({
                            ...ts,
                            useCustomerSlot: true,
                            carrierTimeslot: null,
                          })),
                        );
                      }}
                    />
                  </div>
                  {form.values.timeslots.map((timeslot, index) => (
                    <div key={timeslot.locationId + index}>
                      <TimeslotItem
                        timeslot={timeslot}
                        index={index}
                        disableCarrierQuoteTimeslots={disableCarrierQuoteTimeslots}
                        onChange={(locationId: string, updatedTimeslot: Timeslot | null) => {
                          console.log('Timeslot change:', { locationId, updatedTimeslot });
                          const newTimeslots = [...(form.values.timeslots || [])];
                          const idx = newTimeslots.findIndex(ts => ts.locationId === locationId);
                          if (idx === -1) return;

                          if (updatedTimeslot === null) {
                            newTimeslots.splice(idx, 1);
                            if (newTimeslots.length === 0) {
                              setTimeslotsEnabled(false);
                              form.setFieldValue(form.names.timeslots, null);
                              return;
                            }
                          } else {
                            newTimeslots[idx] = updatedTimeslot;
                          }

                          form.setFieldValue(form.names.timeslots, newTimeslots);
                        }}
                        validationErrors={form.validationErrors}
                      />
                      {index < form.values.timeslots.length - 1 && (
                        <div className="h-px w-full bg-content-tertiary my-6" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {offerNoteEnabled && (
                <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                  <div className="flex flex-row justify-between pb-6">
                    <COHeadline
                      variant="h3"
                      title={tr('carrier.demand.quote.note.title', 'Offer note')}
                    />
                    <RingilButton
                      mode={'table'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        setOfferNoteEnabled(false);
                        form.setFieldValue(form.names.note, null);
                      }}
                    />
                  </div>
                  <Field
                    label={tr('carrier.demand.quote.offer.note.label', 'Offer note')}>
                    <InputTextarea
                      value={form.values.note}
                      onChange={e => form.setFieldValue(form.names.note, e.target.value)}
                      rows={4}
                    />
                  </Field>
                </div>
              )}


              <div className="flex flex-col items-center justify-center">
                {!transportLegsEnabled && (
                  <RingilButton
                    mode={'text-only'}
                    icon={<SvgPlus />}
                    label={tr('carrier.demand.quote.button.transportLegs.label', 'Add transport legs')}
                    onClick={() => {
                      form.setFieldValue(form.names.transportLegs, [
                        {
                          transportationType: TransportationTypeEnum.GROUND,
                          position: 0,
                        },
                      ]);
                      setTransportLegsEnabled(true);
                    }}

                  />
                )}
                {offerType === OfferTypeEnum.SPOT && !timeslotsEnabled && !disableCarrierQuoteTimeslots && (
                  <RingilButton
                    mode={'text-only'}
                    icon={<SvgPlus />}
                    label={tr('carrier.demand.quote.button.timeSlots.label', 'Add custom time slots')}
                    onClick={() => setTimeslotsEnabled(true)}
                  />
                )}
                {!offerNoteEnabled && (
                  <RingilButton
                    mode={'text-only'}
                    icon={<SvgPlus />}
                    label={tr('carrier.demand.quote.button.note.label', 'Add offer note')}
                    onClick={() => setOfferNoteEnabled(true)}
                  />
                )}
              </div>
            </div>
          )}

          {!infoEditMode && (
            <div className="flex flex-col gap-4">
              {quote.transportLegs && (
                <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                  <div className="flex flex-row pb-6 items-center justify-center">
                    <COHeadline
                      variant="h3"
                      title={tr('carrier.demand.quote.transportLegs.title', 'Transport legs')}
                    />
                  </div>
                  {quote.transportLegs.map((leg, idx) => (
                    <div key={idx}>
                      <div className="flex flex-row gap-2">
                        <div className="flex items-center justify-center">
                          {getTransportIcon(leg.transportationType)}
                        </div>
                        <div className="flex flex-col text-content-primary font-dm-sans text-[14px] font-normal">
                          {tr('carrier.demand.quote.transportationType.' + leg.transportationType.toLowerCase() + '.prefix', `${leg.transportationType}: `)}
                          {joinLegInfo(leg)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}


              <div className="flex flex-col p-8 rounded-[4px] bg-base-tertiary">
                <div className="flex flex-row pb-6 items-center justify-center">
                  <COHeadline
                    variant="h2"
                    title={tr('carrier.demand.quote.selectedTimeslots.title', 'Selected timeslots')}
                  />
                </div>
                {quote.timeslots.map((timeslot, index) => (
                  <div key={timeslot.locationId + index}>
                    <div className="flex flex-row gap-2">
                      <div className="flex items-center justify-center">
                        <SvgMapPin />
                      </div>
                      <div className="flex flex-col text-black font-dm-sans text-[16px] font-medium tracking-[-0.16px]">
                        <span data-testid={`carrier-demand-selectedTimeslots-location-${index}-locationName`}>{timeslot.locationName}</span>
                        <span>{`${timeslot.streetNr} ${timeslot.zipCode} ${timeslot.city}`}</span>
                        <COTimezone dataTestId={`carrier-demand-selectedTimeslots-location-${index}-timezone`} timezoneId={timeslot.timeslots[0]?.timezoneId} />
                        {timeslot.timeslots.filter(slot => slot.isSelected).map((slot, idx, arr) => {
                          const slotSince = slot.utcStart ? dayjsFromApiUtc(slot.utcStart): dayjsFromApiPrague(slot.sinceInUTC);
                          const slotTill = slot.utcEnd ? dayjsFromApiUtc(slot.utcEnd) : dayjsFromApiPrague(slot.tillInUTC);
                          const timezoneId = slot.timezoneId || 'Europe/Prague'
                          return (
                            <span  key={idx} data-testid={`carrier-demand-selectedTimeslots-location-${index}-slot-${idx}`}>
                              {formatDateFromDayjsInTzWithLocale(slotSince, timezoneId)}
                              <span className="mx-2 text-content-tertiary">|</span>
                              {formatTimeFromDayjsInTz(slotSince, timezoneId)} - {formatTimeFromDayjsInTz(slotTill, timezoneId)}
                              {idx < arr.length - 1 && <span>, </span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    {index < form.values.timeslots.length - 1 && (
                      <div className="h-px w-full bg-content-tertiary my-6" />
                    )}
                  </div>
                ))}
              </div>


              {quote.note && (
                <div className="flex flex-col p-8 items-center">
                  <COHeadline
                    variant="h4"
                    title={tr('carrier.demand.quote.note.title', 'Offer note')}
                  />
                  <div
                    className="text-black font-dm-sans text-[14px] font-normal text-center break-words whitespace-normal">
                    {quote.note}
                  </div>
                </div>
              )}
            </div>
          )}

          {(priceEditMode || infoEditMode)
            && !quote.isAssignedToAnotherCarrier
            && (
            <>
              <div>
                <InputCheckbox
                  label={tr(
                    `carrier.demand.quote.offer.checkbox.confirmation.label`,
                    `We confirm that we are able to fulfill required time slots for this shipment`,
                  )}
                  value={form.values.isConfirmed}
                  onChange={(_, value) => form.setFieldValue(form.names.isConfirmed, value)}
                  dataTestId="carrier-demand-quote-confirm"
                  toggleOnLabelClick
                />
              </div>
              <div ref={acceptButtonsRef} className="flex flew-row mt-4 gap-4 items-center justify-center">
                <RingilButton
                  mode={'filled-rectangle'}
                  label={isContractedQuote
                    ? tr(`carrier.demand.quote.contracted.button.accept`, `Přijmout cenu`)
                    : tr(`carrier.demand.quote.offer.button.submitQuote.label`, `Submit quote`)}
                  disabled={!form.values.isConfirmed || (isContractedQuote && contractedOfferTotal === null)}
                  loading={loading}
                  onClick={() => form.validateAndSend()}
                  dataTestId="carrier-demand-quote-submit"
                />
                {quote.carrierQuoteState === CarrierQuoteStateEnum.OFFERED_PRICE && (
                  <RingilButton
                    mode={'text-only'}
                    label={tr(`carrier.demand.quote.offer.button.cancelEdit.label`, `Cancel edit`)}
                    loading={loading}
                    onClick={() => {
                      setPriceEditMode(false);
                      setInfoEditMode(false);
                    }}
                    dataTestId="carrier-demand-quote-cancelEdit"
                  />
                )}
                {(quote.carrierQuoteState === CarrierQuoteStateEnum.NEW || (isNullOrUndefined(quote.carrierQuoteState) && offerType === OfferTypeEnum.FIXED && quote.customerQuoteState !== CustomerQuoteStateEnum.REJECTED_BY_CARRIER)) && (
                  <RingilButton
                    mode={'outlined-rectangle'}
                    label={tr(`carrier.demand.quote.offer.button.reject.label`, `Reject demand`)}
                    icon={<SvgClose />}
                    loading={loading}
                    onClick={() => setRejectDialogVisible(true)}
                    dataTestId="carrier-demand-quote-reject"
                  />
                )}
              </div>
              {isMyQuoteBeforeDistribution && (
                <COInlineMessage variant="warning">
                  {tr(
                    'carrier.demand.quote.warning.myQuoteBeforeDistribution',
                    'Your offer valid till is before distribution valid till',
                  )}
                </COInlineMessage>
              )}
            </>
          )}

          {!effectiveDistributionExpired
            && !(priceEditMode || infoEditMode)
            && quote.carrierQuoteState !== CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER
            && quote.carrierQuoteState !== CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM
            && !quote.isAssignedToAnotherCarrier
            && quote.lastExpeditionStatus !== LastExpeditionStatusEnum.ASSIGNED
            && (isNullOrUndefined(quote.ladenFactor) || quote.remainingManipulationUnits > 0)
            && (
            <>
              <div className="flex flew-row mt-4 gap-4 items-center justify-center">
                <RingilButton
                  mode={'outlined-rectangle'}
                  label={tr(`carrier.demand.quote.offer.button.editQuote.label`, `Edit quote`)}
                  icon={<SvgPencil />}
                  loading={loading}
                  onClick={() => {
                    setPriceEditMode(true);
                    setInfoEditMode(true);
                  }}
                  dataTestId="carrier-demand-quote-edit"
                />
                {(
                  quote.carrierQuoteState !== CarrierQuoteStateEnum.REJECTED_BY_CUSTOMER
                  && quote.carrierQuoteState !== CarrierQuoteStateEnum.REJECTED_BY_CARRIER
                  && quote.customerQuoteState !== CustomerQuoteStateEnum.REJECTED_BY_CARRIER
                ) && (
                  <RingilButton
                    mode={'outlined-rectangle'}
                    label={tr(`carrier.demand.quote.offer.button.reject.label`, `Reject demand`)}
                    icon={<SvgClose />}
                    loading={loading}
                    onClick={() => setRejectDialogVisible(true)}
                    dataTestId="carrier-demand-quote-reject"
                  />
                )}
              </div>
              {quoteValidTillVisible && !isContractedQuote && (
                <div
                  className="flex flex-col mt-4 items-center justify-center text-content-primary font-dm-sans text-[13px] font-normal"
                  data-testid="carrier-demand-quote-validTill">
                  {tr('carrier.demand.quote.offer.validTillUtc', 'Offer valid till {{-date}}',
                    { date: formatDateTimeFromDayjsInTzWithLocale(form.values.validTillLocalTime, timezoneToUse) })}
                  <COTimezone dataTestId="carrier-demand-quote-validTill-timezone" timezoneId={timezoneToUse} />
                </div>
              )}
            </>
          )}

        </div>
      </div>

      <RejectDialogComponent
        visible={rejectDialogVisible}
        onHide={() => setRejectDialogVisible(false)}
        onReject={(onComplete) => {
          onReject();
          setRejectDialogVisible(false);
          onComplete();
        }}
        disabled={false}
      />

    </div>
  );
};

export default CarrierQuote;
